import React, { useState, useEffect, useRef } from 'react';
import { 
  // Exterior Cleaning
  Droplet,       // PressureWashing
  Sparkles,      // WindowWashing
  Home,          // GutterCleaning, HouseCleaning
  
  // Lawn & Landscape
  Leaf,          // Landscaping
  Trees,         // TreeService
  
  // Seasonal
  Lightbulb,     // ChristmasLights
  
  // Specialty
  Square,        // GlassMirror
  Car,           // MobileDetailing
  
  // Trades
  Thermometer,   // HVAC
  Wrench,        // Plumbing
  Zap,           // Electrical
  Hammer,        // Handyman
  
  // Pool & Pest
  Waves,         // PoolService
  Bug,           // PestControl
  
  // Other
  Truck,         // JunkRemoval
  Briefcase,     // Other/fallback
  
  // UI Icons
  Check,
  Plus,
  X
} from 'lucide-react';
import { industryService } from '../services/industryService';
import BottomSheet from './BottomSheet';

// Icon mapping for field service types
const iconMap = {
  // Exterior Cleaning
  droplet: Droplet,
  sparkles: Sparkles,
  
  // Interior
  home: Home,
  
  // Lawn & Landscape  
  leaf: Leaf,
  trees: Trees,
  
  // Seasonal
  lightbulb: Lightbulb,
  
  // Specialty
  square: Square,
  car: Car,
  
  // Trades
  thermometer: Thermometer,
  wrench: Wrench,
  zap: Zap,
  hammer: Hammer,
  
  // Pool & Pest
  waves: Waves,
  bug: Bug,
  
  // Other
  truck: Truck,
  briefcase: Briefcase,
};

const IndustrySelector = ({ value, onChange, additionalServices = [], onAdditionalChange, showPreview = true }) => {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Bottom sheet state (only for secondary services now)
  const [secondarySheetOpen, setSecondarySheetOpen] = useState(false);
  
  // Ref for horizontal scroll
  const scrollContainerRef = useRef(null);

  // Load service types on mount
  useEffect(() => {
    loadServiceTypes();
  }, []);

  // Update preview when selections change
  useEffect(() => {
    if (value && showPreview) {
      loadPreview();
    }
  }, [value, additionalServices, showPreview]);

  const loadServiceTypes = async () => {
    try {
      setLoading(true);
      const data = await industryService.getServiceTypeSummary();
      setServiceTypes(data);
    } catch (error) {
      console.error('Error loading service types:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    try {
      setPreviewLoading(true);
      const data = await industryService.previewTemplates(value, additionalServices);
      setPreview(data);
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Direct select on pill click - no bottom sheet for primary
  const handlePillClick = (service) => {
    onChange(service.serviceType);
    
    // Remove from additional services if it was selected there
    if (additionalServices.includes(service.serviceType)) {
      onAdditionalChange(additionalServices.filter(s => s !== service.serviceType));
    }
  };

  const handleSecondaryToggle = (serviceType) => {
    if (additionalServices.includes(serviceType)) {
      onAdditionalChange(additionalServices.filter(s => s !== serviceType));
    } else {
      onAdditionalChange([...additionalServices, serviceType]);
    }
  };

  const removeSecondary = (serviceType) => {
    onAdditionalChange(additionalServices.filter(s => s !== serviceType));
  };

  const getIcon = (iconClass) => {
    if (!iconClass) return Briefcase;
    const normalizedClass = iconClass.toLowerCase().replace(/-/g, '');
    return iconMap[normalizedClass] || Briefcase;
  };

  const getSelectedPrimaryService = () => {
    return serviceTypes.find(s => s.serviceType === value);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading service types...</p>
      </div>
    );
  }

  const selectedPrimary = getSelectedPrimaryService();

  return (
    <div className="space-y-6">
      {/* Primary Service Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Primary Service Type *
        </label>
        <p className="text-sm text-gray-600 mb-4">
          Tap to select your main service - this sets your app's theme and terminology
        </p>
        
        {/* Selected Primary Card */}
        {selectedPrimary && (
          <div 
            className="mb-4 p-4 rounded-xl border-2 bg-blue-50"
            style={{ borderColor: selectedPrimary.primaryColor }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: selectedPrimary.primaryColor + '20' }}
                >
                  {React.createElement(getIcon(selectedPrimary.iconClass), {
                    size: 24,
                    style: { color: selectedPrimary.primaryColor }
                  })}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {selectedPrimary.displayName}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {selectedPrimary.templateCount} {selectedPrimary.jobTerm.toLowerCase()} • Primary
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div 
                  className="w-4 h-4 rounded-full border-2 border-white shadow-sm" 
                  style={{ backgroundColor: selectedPrimary.primaryColor }}
                />
                <Check size={20} style={{ color: selectedPrimary.primaryColor }} />
              </div>
            </div>
          </div>
        )}
        
        {/* Service count indicator */}
        {!value && serviceTypes.length > 0 && (
          <p className="text-xs text-blue-600 font-medium mb-2 sm:hidden">
            {serviceTypes.length} service types available — swipe to browse
          </p>
        )}
        
        {/* Horizontal Scrolling Pills (mobile) / Wrapped Grid (desktop) */}
        <div className="relative">
          <div 
            ref={scrollContainerRef}
            className="
              flex flex-wrap gap-2
              sm:flex-wrap sm:gap-3
              max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:pb-2 max-sm:scrollbar-hide max-sm:-mx-4 max-sm:px-4
            "
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {serviceTypes.map((service) => {
              const Icon = getIcon(service.iconClass);
              const isSelected = value === service.serviceType;
              
              return (
                <button
                  key={service.serviceType}
                  type="button"
                  onClick={() => handlePillClick(service)}
                  className={`
                    flex-shrink-0 flex items-center space-x-2 px-4 py-2.5 rounded-full
                    border-2 transition-all whitespace-nowrap
                    ${isSelected 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 text-gray-700'
                    }
                  `}
                  style={isSelected ? { borderColor: service.primaryColor } : {}}
                >
                  <Icon size={18} style={isSelected ? { color: service.primaryColor } : {}} />
                  <span className="font-medium text-sm">{service.displayName}</span>
                  {isSelected && <Check size={16} style={{ color: service.primaryColor }} />}
                </button>
              );
            })}
          </div>
          
          {/* Fade edge to indicate scrollability - mobile only */}
          <div className="absolute right-0 top-0 bottom-2 w-12 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none sm:hidden" />
        </div>
        
        {!value && (
          <p className="text-xs text-gray-500 mt-2 sm:hidden">
            ← Scroll to see all options
          </p>
        )}
      </div>

      {/* Additional Services Section */}
      {value && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Services (Optional)
          </label>
          <p className="text-sm text-gray-600 mb-3">
            Add more service types to get extra job templates
          </p>
          
          {/* Selected Secondary Services Pills */}
          {additionalServices.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {additionalServices.map((serviceType) => {
                const service = serviceTypes.find(s => s.serviceType === serviceType);
                if (!service) return null;
                const Icon = getIcon(service.iconClass);
                
                return (
                  <div 
                    key={serviceType}
                    className="flex items-center space-x-2 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200"
                  >
                    <Icon size={14} className="text-gray-600" />
                    <span className="text-sm text-gray-700">{service.displayName}</span>
                    <button
                      type="button"
                      onClick={() => removeSecondary(serviceType)}
                      className="p-0.5 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      <X size={14} className="text-gray-500" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Add Secondary Button */}
          <button
            type="button"
            onClick={() => setSecondarySheetOpen(true)}
            className="flex items-center space-x-2 px-4 py-2.5 rounded-lg border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all w-full sm:w-auto justify-center sm:justify-start"
          >
            <Plus size={18} />
            <span className="font-medium text-sm">
              {additionalServices.length > 0 ? 'Add More Services' : 'Add Secondary Services'}
            </span>
          </button>
        </div>
      )}

      {/* Preview Section */}
      {showPreview && value && preview && (
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4 sm:p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Your Service Setup
              </h3>
              <p className="text-sm text-gray-600">
                We'll pre-configure these services for you
              </p>
            </div>
            
            {previewLoading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            ) : (
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">
                  {preview.totalCount}
                </div>
                <div className="text-xs text-gray-600">
                  services ready
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            {preview.serviceTypes.map((serviceGroup) => {
              const service = serviceTypes.find(s => s.serviceType === serviceGroup.serviceType);
              const Icon = service ? getIcon(service.iconClass) : Briefcase;
              
              return (
                <div 
                  key={serviceGroup.serviceType}
                  className="bg-white rounded-lg p-3 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon size={16} style={{ color: service?.primaryColor || '#3b82f6' }} />
                      <span className="font-medium text-sm text-gray-900">
                        {service?.displayName || serviceGroup.serviceType}
                      </span>
                    </div>
                    <span className="text-xs font-semibold text-blue-600">
                      {serviceGroup.count} services
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    {serviceGroup.templates.slice(0, 5).map((template, idx) => (
                      <span 
                        key={idx}
                        className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                      >
                        {template.name}
                      </span>
                    ))}
                    {serviceGroup.templates.length > 5 && (
                      <span className="text-xs text-gray-500 px-2 py-1">
                        +{serviceGroup.templates.length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-blue-200">
            <p className="text-xs text-gray-600 text-center">
              ✨ You can customize or add more services after registration
            </p>
          </div>
        </div>
      )}

      {/* Secondary Services Bottom Sheet */}
      <BottomSheet
        isOpen={secondarySheetOpen}
        onClose={() => setSecondarySheetOpen(false)}
        title="Add Secondary Services"
        maxHeight="70vh"
      >
        <div className="space-y-2">
          <p className="text-sm text-gray-600 mb-4">
            Select additional services you offer. We'll include their job templates too!
          </p>
          
          {serviceTypes
            .filter(s => s.serviceType !== value) // Exclude primary
            .map((service) => {
              const Icon = getIcon(service.iconClass);
              const isSelected = additionalServices.includes(service.serviceType);
              
              return (
                <button
                  key={service.serviceType}
                  type="button"
                  onClick={() => handleSecondaryToggle(service.serviceType)}
                  className={`
                    w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all
                    ${isSelected 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <div 
                      className={`
                        w-10 h-10 rounded-lg flex items-center justify-center
                        ${isSelected ? 'bg-white' : 'bg-gray-100'}
                      `}
                    >
                      <Icon 
                        size={20} 
                        className={isSelected ? 'text-blue-600' : 'text-gray-600'}
                      />
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium text-gray-900">{service.displayName}</h4>
                      <p className="text-xs text-gray-500">+{service.templateCount} templates</p>
                    </div>
                  </div>
                  
                  <div className={`
                    w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
                    ${isSelected 
                      ? 'border-blue-600 bg-blue-600' 
                      : 'border-gray-300 bg-white'
                    }
                  `}>
                    {isSelected && <Check size={14} className="text-white" />}
                  </div>
                </button>
              );
            })}
          
          {/* Done Button */}
          <div className="pt-4 mt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setSecondarySheetOpen(false)}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
            >
              Done ({additionalServices.length} selected)
            </button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
};

export default IndustrySelector;